import type { KeyboardEvent, ReactNode } from 'react'
import type { HeroCoordinates } from '@/components/heroes/hero-frame'
import type { CSSVars } from '@/types/css'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { onAdapted } from '@/lib/adaptation'
import { loadStars } from '@/lib/astro/catalogue'
import { prefersReducedMotion } from '@/lib/preferences'
import { registerSkyPointer } from '@/lib/sky-pointer'
import { FadeLink } from '@/components/generals/fade-link'
import { HeroFrame, useHero } from '@/components/heroes/hero-frame'
import { guideBySlug } from '@/constants/guides'
import styles from '@/components/heroes/learn-hero.module.css'

const TITLE_ID = 'learn-hero-title'

/** The middle of the Plough, shown in the corner label until a star is pointed at. */
const PLOUGH_CENTRE: HeroCoordinates = { ra: 12.3, dec: 56 }

const NORTH_STAR_GUIDE = guideBySlug('find-the-north-star')

type Point = { x: number, y: number }
type Phase = 'idle' | 'playing' | 'still'

/* ------------------------------------------------------------------------------------------------
   Projection: polar stereographic about the north celestial pole, drawn as the sky looks facing
   north, so every circle of declination is a true circle around the pole. All of the chart below is
   computed once at module load and rounded, so the server and the browser draw exactly the same SVG.
   ------------------------------------------------------------------------------------------------ */

const DEG = Math.PI / 180
/** Chart units for a stereographic radius of one. */
const SCALE = 1000
/** The right ascension straight up from the pole. 12.3 h hangs straight down, as the Plough does on autumn evenings. */
const TOP_RA = 0.3

const round = (value: number): number => Math.round(value * 10) / 10

/** The chart radius of a circle of declination. */
const radiusOf = (dec: number): number => 2 * Math.tan(((90 - dec) * DEG) / 2) * SCALE

const projectExact = (ra: number, dec: number): Point => {
    const radius = radiusOf(dec)
    const angle = (ra - TOP_RA) * 15 * DEG

    return { x: radius * Math.sin(angle), y: -radius * Math.cos(angle) }
}

const project = (ra: number, dec: number): Point => {
    const point = projectExact(ra, dec)

    return { x: round(point.x), y: round(point.y) }
}

/** Chart units back to J2000 coordinates, for the reticle cursor. */
const unproject = (x: number, y: number): HeroCoordinates => {
    const radius = Math.hypot(x, y) / SCALE
    const hours = Math.atan2(x, -y) / DEG / 15 + TOP_RA

    return { ra: ((hours % 24) + 24) % 24, dec: 90 - (2 * Math.atan(radius / 2)) / DEG }
}

/** A point at a chart radius and an angle measured clockwise from straight up. */
const polar = (radius: number, degrees: number): Point => ({
    x: round(radius * Math.sin(degrees * DEG)),
    y: round(-radius * Math.cos(degrees * DEG))
})

const VIEW = { x: -320, y: -100, width: 640, height: 945 }
const VIEW_BOX = `${VIEW.x} ${VIEW.y} ${VIEW.width} ${VIEW.height}`

/* ----------------------------------------- the stars ----------------------------------------- */

type LabelAnchor = 'start' | 'middle' | 'end'

type StarSpec = {
    key: string
    name: string
    /** Read out with the name by screen readers. */
    note: string
    /** J2000 right ascension in hours. */
    ra: number
    /** J2000 declination in degrees. */
    dec: number
    mag: number
    /** Where the name sits, in screen pixels from the star, and how it is anchored. */
    label: [number, number, LabelAnchor]
    /** Merak and Dubhe keep their names on show once the figure is drawn. */
    pinned?: boolean
}

/** The seven stars of the Plough, from the tip of the handle to the pointers, in the order they light. */
const PLOUGH: StarSpec[] = [
    { key: 'alkaid', name: 'Alkaid', note: 'the tip of the handle', ra: 13.792344, dec: 49.313267, mag: 1.86, label: [-13, 0, 'end'] },
    { key: 'mizar', name: 'Mizar', note: 'in the bend of the handle, with its faint companion Alcor', ra: 13.398761, dec: 54.925352, mag: 2.23, label: [0, 17, 'middle'] },
    { key: 'alioth', name: 'Alioth', note: 'the brightest star of the Plough', ra: 12.900486, dec: 55.959823, mag: 1.77, label: [0, 17, 'middle'] },
    { key: 'megrez', name: 'Megrez', note: 'the faintest of the seven, where the handle meets the pan', ra: 12.2571, dec: 57.032616, mag: 3.31, label: [-11, -11, 'end'] },
    { key: 'phecda', name: 'Phecda', note: 'the lower corner of the pan', ra: 11.89718, dec: 53.694759, mag: 2.44, label: [11, 11, 'start'] },
    { key: 'merak', name: 'Merak', note: 'the lower of the two pointers', ra: 11.030688, dec: 56.382426, mag: 2.37, label: [13, 0, 'start'], pinned: true },
    { key: 'dubhe', name: 'Dubhe', note: 'the upper pointer, nearer Polaris', ra: 11.062131, dec: 61.751035, mag: 1.79, label: [13, 0, 'start'], pinned: true }
]

const POLARIS: StarSpec = {
    key: 'polaris',
    name: 'Polaris',
    note: 'the North Star, less than a degree from the pole',
    ra: 2.530304,
    dec: 89.264109,
    mag: 1.98,
    label: [0, 0, 'start']
}

const ALCOR = { ra: 13.420428, dec: 54.987959 }

/** Hipparcos numbers of the stars drawn by hand, left out of the faint background field. */
const CHART_HIP = new Set([54061, 53910, 58001, 59774, 62956, 65378, 65477, 67301, 11767])

type ChartStar = StarSpec & Point & {
    r: number
    delay: number
    pole: boolean
}

const STAR_AT = 1200
const STAR_STEP = 240

const starRadius = (mag: number): number => round(1.6 + (4.2 - mag) * 1.5)

const plot = (spec: StarSpec, delay: number, pole = false): ChartStar => ({
    ...spec,
    ...project(spec.ra, spec.dec),
    r: starRadius(spec.mag),
    delay,
    pole
})

const PLOUGH_STARS = PLOUGH.map((spec, index) => plot(spec, STAR_AT + index * STAR_STEP))
const BY_KEY = Object.fromEntries(PLOUGH_STARS.map(star => [star.key, star])) as Record<string, ChartStar>

/* ------------------------- the figure, drawn from the handle to the pointers ------------------------- */

type Segment = {
    key: string
    d: string
    start: number
    duration: number
    pointer: boolean
}

const LINE_GAP = 9
const LINES_AT = 2850

const segment = (fromKey: string, toKey: string, start: number, pointer = false): Segment => {
    const from = BY_KEY[fromKey]
    const to = BY_KEY[toKey]
    const dx = to.x - from.x
    const dy = to.y - from.y
    const length = Math.hypot(dx, dy)
    const a = (from.r + LINE_GAP) / length
    const b = 1 - (to.r + LINE_GAP) / length

    return {
        key: `${fromKey}-${toKey}`,
        d: `M ${round(from.x + dx * a)} ${round(from.y + dy * a)} L ${round(from.x + dx * b)} ${round(from.y + dy * b)}`,
        start,
        duration: Math.round(120 + length * (b - a) * 1.5),
        pointer
    }
}

const endOf = (item: Segment): number => item.start + item.duration

const HANDLE_TIP = segment('alkaid', 'mizar', LINES_AT)
const HANDLE_MID = segment('mizar', 'alioth', endOf(HANDLE_TIP))
const HANDLE_ROOT = segment('alioth', 'megrez', endOf(HANDLE_MID))
const PAN_RIM = segment('megrez', 'dubhe', endOf(HANDLE_ROOT))
const PAN_SIDE = segment('megrez', 'phecda', endOf(HANDLE_ROOT))
const PAN_BASE = segment('phecda', 'merak', endOf(PAN_SIDE))
const POINTERS = segment('merak', 'dubhe', Math.max(endOf(PAN_RIM), endOf(PAN_BASE)), true)

const SEGMENTS = [HANDLE_TIP, HANDLE_MID, HANDLE_ROOT, PAN_RIM, PAN_SIDE, PAN_BASE, POINTERS]
const PINNED_AT = endOf(POINTERS) - 100

/* ------------------------- the pointers, carried on five times their gap ------------------------- */

const DUBHE = BY_KEY.dubhe
const MERAK = BY_KEY.merak
const POLARIS_POINT = project(POLARIS.ra, POLARIS.dec)
const pointerLength = Math.hypot(POLARIS_POINT.x - DUBHE.x, POLARIS_POINT.y - DUBHE.y)
const ux = (POLARIS_POINT.x - DUBHE.x) / pointerLength
const uy = (POLARIS_POINT.y - DUBHE.y) / pointerLength
/** The gap between the two pointers: the unit of the "five times" rule. */
const pointerGap = Math.hypot(DUBHE.x - MERAK.x, DUBHE.y - MERAK.y)

/** A point a distance along the pointer line from Dubhe, and an offset to its right. */
const along = (distance: number, offset = 0): Point => ({
    x: round(DUBHE.x + ux * distance - uy * offset),
    y: round(DUBHE.y + uy * distance + ux * offset)
})

type Line = Point & {
    key: string
    x2: number
    y2: number
    delay: number
}

const DASH = 9
const DASH_SPACE = 7
const DASH_MS = 32
const DASH_FROM = DUBHE.r + 13
const DASH_TO = pointerLength - 44
const DASH_COUNT = Math.floor((DASH_TO - DASH_FROM + DASH_SPACE) / (DASH + DASH_SPACE))
const POINTER_AT = endOf(POINTERS) + 80

const DASHES: Line[] = Array.from({ length: DASH_COUNT }, (_, index) => {
    const start = DASH_FROM + index * (DASH + DASH_SPACE)
    const a = along(start)
    const b = along(start + DASH)

    return { key: `dash-${index}`, x: a.x, y: a.y, x2: b.x, y2: b.y, delay: POINTER_AT + index * DASH_MS }
})

const dashDelayAt = (distance: number): number => POINTER_AT + Math.max(0, Math.round((distance - DASH_FROM) / (DASH + DASH_SPACE))) * DASH_MS

const GAP_TICKS: Line[] = [1, 2, 3, 4].map(step => {
    const a = along(pointerGap * step, -8)
    const b = along(pointerGap * step, 8)

    return { key: `gap-${step}`, x: a.x, y: a.y, x2: b.x, y2: b.y, delay: dashDelayAt(pointerGap * step) }
})

const CAPTION = {
    ...along(pointerGap * 2.5, 24),
    angle: round(Math.atan2(uy, ux) / DEG),
    delay: POINTER_AT + 520
}

const POLARIS_AT = POINTER_AT + DASH_COUNT * DASH_MS - 120
const POLARIS_STAR = plot(POLARIS, POLARIS_AT, true)
const POLARIS_LABEL = { x: 44, y: -56, delay: POLARIS_AT + 350 }
const STORY_AT = POLARIS_AT + 450

const CHART_STARS: ChartStar[] = [...PLOUGH_STARS, POLARIS_STAR]

/* ---------------------- the frame: a graduated ring and a declination scale ---------------------- */

type Tick = Point & {
    key: string
    x2: number
    y2: number
    strong: boolean
    delay: number
}

type TickLabel = Point & {
    key: string
    text: string
}

/** The circle of declination +45°, just outside the handle, graduated in right ascension. */
const RING_RADIUS = radiusOf(45)
const RING_SPAN = 36
const ringFrom = polar(RING_RADIUS, 180 - RING_SPAN)
const ringTo = polar(RING_RADIUS, 180 + RING_SPAN)
const RING_PATH = `M ${ringFrom.x} ${ringFrom.y} A ${round(RING_RADIUS)} ${round(RING_RADIUS)} 0 0 1 ${ringTo.x} ${ringTo.y}`

const FIRST_MINUTE = Math.ceil(((180 - RING_SPAN) / 15 + TOP_RA) * 6) * 10
const LAST_MINUTE = Math.floor(((180 + RING_SPAN) / 15 + TOP_RA) * 6) * 10
const RING_MINUTES = Array.from({ length: Math.round((LAST_MINUTE - FIRST_MINUTE) / 10) + 1 }, (_, index) => FIRST_MINUTE + index * 10)

const RING_TICKS: Tick[] = RING_MINUTES.map((minute, index) => {
    const angle = (minute / 60 - TOP_RA) * 15
    const length = minute % 60 === 0 ? 18 : minute % 30 === 0 ? 11 : 6
    const outer = polar(RING_RADIUS, angle)
    const inner = polar(RING_RADIUS - length, angle)

    return { key: `ra-${minute}`, x: outer.x, y: outer.y, x2: inner.x, y2: inner.y, strong: minute % 60 === 0, delay: 500 + index * 30 }
})

const RING_LABELS: TickLabel[] = RING_MINUTES
    .filter(minute => minute % 60 === 0)
    .map(minute => ({ key: `ra-label-${minute}`, ...polar(RING_RADIUS - 36, (minute / 60 - TOP_RA) * 15), text: `${minute / 60}h` }))

/** A crosshair through the pole, graduated in declination from +88° to +75°. */
const POLE_RING = round(radiusOf(88))
const SCALE_LAST = 75
const SCALE_END = round(radiusOf(SCALE_LAST) + 16)

const DEC_TICKS: Tick[] = Array.from({ length: 88 - SCALE_LAST + 1 }, (_, index) => 88 - index).flatMap(dec => {
    const x = round(radiusOf(dec))
    const strong = dec % 5 === 0
    const half = strong ? 9 : 4

    return [-1, 1].map(side => ({ key: `dec-${dec}-${side}`, x: side * x, y: -half, x2: side * x, y2: half, strong, delay: 700 + (88 - dec) * 45 }))
})

const DEC_LABELS: TickLabel[] = [80, 75].flatMap(dec => [-1, 1].map(side => ({
    key: `dec-label-${dec}-${side}`,
    x: side * round(radiusOf(dec)),
    y: 0,
    text: `+${dec}°`
})))

const CROSSHAIR = [
    `M ${POLE_RING} 0 L ${SCALE_END} 0`,
    `M ${-POLE_RING} 0 L ${-SCALE_END} 0`,
    `M 0 ${-POLE_RING} L 0 ${round(-POLE_RING - 46)}`,
    `M 0 ${POLE_RING} L 0 ${round(POLE_RING + 46)}`
]

const POLE_RING_PATH = `M 0 ${-POLE_RING} A ${POLE_RING} ${POLE_RING} 0 1 1 0 ${POLE_RING} A ${POLE_RING} ${POLE_RING} 0 1 1 0 ${-POLE_RING}`

/* -------------------------- Mizar and Alcor, magnified in a small eyepiece -------------------------- */

const MIZAR = BY_KEY.mizar
const mizarExact = projectExact(MIZAR.ra, MIZAR.dec)
const alcorExact = projectExact(ALCOR.ra, ALCOR.dec)
const INSET_ZOOM = 7
const INSET_RADIUS = 30
const insetX = round(MIZAR.x - 6)
const insetY = round(MIZAR.y - 92)
const pairX = ((alcorExact.x - mizarExact.x) * INSET_ZOOM) / 2
const pairY = ((alcorExact.y - mizarExact.y) * INSET_ZOOM) / 2
const leaderLength = Math.hypot(insetX - MIZAR.x, insetY - MIZAR.y)
const leaderX = (insetX - MIZAR.x) / leaderLength
const leaderY = (insetY - MIZAR.y) / leaderLength

const INSET = {
    x: insetX,
    y: insetY,
    mizar: { x: round(insetX - pairX), y: round(insetY - pairY) },
    alcor: { x: round(insetX + pairX), y: round(insetY + pairY) },
    leader: {
        x1: round(MIZAR.x + leaderX * (MIZAR.r + 10)),
        y1: round(MIZAR.y + leaderY * (MIZAR.r + 10)),
        x2: round(insetX - leaderX * (INSET_RADIUS + 4)),
        y2: round(insetY - leaderY * (INSET_RADIUS + 4))
    },
    delay: MIZAR.delay + 700
}

/* ---------------------------------- the faint field behind ---------------------------------- */

type FieldStar = Point & {
    r: number
    opacity: number
}

const FIELD_LIMIT = 5.2
const FIELD = { left: -440, right: 440, top: -230, bottom: 880 }

/* -------------------------------------------- styles -------------------------------------------- */

const timing = (delay: number, duration?: number): CSSVars => {
    const style: CSSVars = { '--d': `${delay}ms` }

    if (duration !== undefined) style['--dur'] = `${duration}ms`

    return style
}

const place = (x: number, y: number, delay?: number, duration?: number): CSSVars => {
    const style: CSSVars = { '--ox': x, '--oy': y }

    if (delay !== undefined) style['--d'] = `${delay}ms`
    if (duration !== undefined) style['--dur'] = `${duration}ms`

    return style
}

const glow = (value: number): CSSVars => ({ '--glow': value })

/* -------------------------------------------- scene -------------------------------------------- */

const LearnScene = (): ReactNode => {
    const { reduced, skipped, setCoordinates, stickyRef } = useHero()
    const stageRef = useRef<HTMLDivElement | null>(null)
    const svgRef = useRef<SVGSVGElement | null>(null)
    const starRefs = useRef<(SVGGElement | null)[]>([])
    const [phase, setPhase] = useState<Phase>('idle')
    const [field, setField] = useState<FieldStar[]>([])
    const [focusIndex, setFocusIndex] = useState(0)

    // Begins as the dark-adaptation intro lifts (straight away on later pages). Reduced motion gets the finished chart.
    useEffect(() => {
        if (reduced || prefersReducedMotion()) {
            setPhase('still')
            return
        }

        let timer = 0
        let fallback = 0

        const begin = () => {
            window.clearTimeout(fallback)
            window.clearTimeout(timer)
            timer = window.setTimeout(() => setPhase(current => current === 'idle' ? 'playing' : current), 240)
        }

        fallback = window.setTimeout(begin, 7000)

        const stop = onAdapted(begin)

        return () => {
            stop()
            window.clearTimeout(timer)
            window.clearTimeout(fallback)
        }
    }, [reduced])

    useEffect(() => {
        if (skipped) setPhase('still')
    }, [skipped])

    // Hairlines and labels stay the same size on screen whatever the size of the chart.
    useEffect(() => {
        const svg = svgRef.current

        if (!svg) return

        const measure = () => {
            const rect = svg.getBoundingClientRect()
            const scale = Math.min(rect.width / VIEW.width, rect.height / VIEW.height)

            if (scale > 0) svg.style.setProperty('--k', (1 / scale).toFixed(3))
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(svg)

        return () => observer.disconnect()
    }, [])

    // Nothing moves while the hero is out of view.
    useEffect(() => {
        const stage = stageRef.current

        if (!stage || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) delete stage.dataset.paused
            else stage.dataset.paused = 'true'
        })

        observer.observe(stage)

        return () => observer.disconnect()
    }, [])

    // The reticle cursor reads real coordinates over the chart.
    useEffect(() => {
        const sticky = stickyRef.current
        const svg = svgRef.current

        if (!sticky || !svg) return

        return registerSkyPointer(sticky, (clientX, clientY) => {
            const rect = svg.getBoundingClientRect()

            if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return null

            const matrix = svg.getScreenCTM()

            if (!matrix) return null

            const inverse = matrix.inverse()

            return unproject(
                inverse.a * clientX + inverse.c * clientY + inverse.e,
                inverse.b * clientX + inverse.d * clientY + inverse.f
            )
        })
    }, [stickyRef])

    // A faint field of the real neighbouring stars, down to about magnitude five.
    useEffect(() => {
        let alive = true

        loadStars()
            .then(catalogue => {
                if (!alive) return

                const stars: FieldStar[] = []

                for (let index = 0; index < catalogue.count; index++) {
                    const mag = catalogue.mag[index]

                    if (mag > FIELD_LIMIT) break
                    if (CHART_HIP.has(catalogue.hip[index])) continue

                    const point = project(catalogue.ra[index] / 15, catalogue.dec[index])

                    if (point.x < FIELD.left || point.x > FIELD.right || point.y < FIELD.top || point.y > FIELD.bottom) continue

                    stars.push({
                        ...point,
                        r: round(Math.max(0.9, (5.6 - mag) * 0.75)),
                        opacity: Math.min(0.75, Math.round((0.3 + (FIELD_LIMIT - mag) * 0.12) * 100) / 100)
                    })
                }

                setField(stars)
            })
            .catch(() => undefined)

        return () => {
            alive = false
        }
    }, [])

    const aimAt = (star: ChartStar | null) => {
        setCoordinates(star ? { ra: star.ra, dec: star.dec } : PLOUGH_CENTRE)
    }

    const handleKey = (event: KeyboardEvent<SVGGElement>, index: number) => {
        const last = CHART_STARS.length - 1
        let next = -1

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1
        else if (event.key === 'Home') next = 0
        else if (event.key === 'End') next = last

        if (next < 0) return

        event.preventDefault()
        setFocusIndex(next)
        starRefs.current[next]?.focus()
    }

    return (
        <div ref={stageRef} className={cn(styles.stage, styles[phase])}>
            <div className={cn('container-page', styles.layout)}>
                <div className={styles.title}>
                    <p className={cn(styles.rise, styles.eyebrow, 'label-mono hidden text-brass sm:block [@media(max-height:520px)]:hidden')} style={timing(120)}>
                        Learn the night sky
                    </p>
                    <h1
                        id={TITLE_ID}
                        className={cn(
                            styles.rise,
                            'display-serif text-[2.75rem] text-star sm:mt-4 sm:text-6xl xl:text-7xl 2xl:text-[5.25rem] [@media(max-height:520px)]:mt-0 [@media(max-height:520px)]:text-[1.75rem]'
                        )}
                        style={timing(260)}
                    >
                        <span className='block [@media(max-height:520px)]:inline'>
                            Short guides
                        </span>
                        {' '}
                        <span className='block [@media(max-height:520px)]:inline'>
                            for long nights
                        </span>
                    </h1>
                </div>

                <div className={cn(styles.story, styles.rise)} style={timing(STORY_AT)}>
                    <p className={cn(styles.storyLabel, 'label-mono flex items-center gap-3 text-brass')}>
                        <span aria-hidden className='block h-px w-8 bg-brass/60' />
                        The Plough · Ursa Major
                    </p>
                    <p className='mt-3 max-w-[28rem] text-[0.9rem] leading-relaxed text-star-2 sm:mt-4 sm:text-lg [@media(max-height:520px)]:mt-0 [@media(max-height:520px)]:text-[0.85rem] [@media(max-height:520px)]:leading-normal'>
                        Seven stars make the Plough, a saucepan to most of us and the hindquarters and tail of the Great Bear. Follow the two at the front of the pan, from Merak through Dubhe, and they point to Polaris. Through the night every other star wheels around it, while the North Star stays put.
                    </p>
                    {NORTH_STAR_GUIDE && (
                        <FadeLink
                            href={`/learn/${NORTH_STAR_GUIDE.slug}`}
                            className='label-mono group mt-3 inline-flex items-center gap-2 py-1.5 text-star transition-colors duration-500 hover:text-brass sm:mt-5 [@media(max-height:520px)]:mt-2'
                        >
                            {NORTH_STAR_GUIDE.title}
                            <ArrowRight aria-hidden className='size-4 text-brass transition-transform duration-500 group-hover:translate-x-1' strokeWidth={1.25} />
                        </FadeLink>
                    )}
                </div>

                <div className={styles.chartArea}>
                    <svg
                        ref={svgRef}
                        viewBox={VIEW_BOX}
                        preserveAspectRatio='xMidYMid meet'
                        role='group'
                        aria-label='A chart of the Plough in Ursa Major as it hangs below Polaris on autumn evenings. The pointer stars Merak and Dubhe lead to Polaris, the North Star. Use the arrow keys to move between the stars.'
                        className={cn(styles.chart, 'absolute inset-0 block size-full overflow-visible')}
                    >
                        {field.length > 0 && (
                            <g aria-hidden className={styles.fade} style={timing(500, 2600)}>
                                {field.map((star, index) => (
                                    <circle key={index} className={styles.fieldStar} cx={star.x} cy={star.y} r={star.r} fill='var(--c-star-2)' opacity={star.opacity} />
                                ))}
                            </g>
                        )}

                        <g aria-hidden>
                            <path className={cn(styles.hair, styles.draw)} d={RING_PATH} pathLength={1} stroke='var(--c-hair-2)' style={timing(0, 2600)} />
                            {RING_TICKS.map(tick => (
                                <line
                                    key={tick.key}
                                    className={cn(styles.hair, styles.fade)}
                                    x1={tick.x}
                                    y1={tick.y}
                                    x2={tick.x2}
                                    y2={tick.y2}
                                    stroke={tick.strong ? 'var(--c-brass-2)' : 'var(--c-hair-2)'}
                                    style={timing(tick.delay, 700)}
                                />
                            ))}
                            {RING_LABELS.map(label => (
                                <g key={label.key} transform={`translate(${label.x} ${label.y})`}>
                                    <text className={cn(styles.text, styles.mono, styles.small, styles.fade)} textAnchor='middle' fill='var(--c-star-3)' style={place(0, 0, 1500, 900)}>
                                        {label.text}
                                    </text>
                                </g>
                            ))}

                            {CROSSHAIR.map((d, index) => (
                                <path key={d} className={cn(styles.hair, styles.draw)} d={d} pathLength={1} stroke='var(--c-hair-2)' style={timing(300 + (index % 2) * 120, 1500)} />
                            ))}
                            {DEC_TICKS.map(tick => (
                                <line
                                    key={tick.key}
                                    className={cn(styles.hair, styles.fade)}
                                    x1={tick.x}
                                    y1={tick.y}
                                    x2={tick.x2}
                                    y2={tick.y2}
                                    stroke={tick.strong ? 'var(--c-brass-2)' : 'var(--c-hair-2)'}
                                    style={timing(tick.delay, 600)}
                                />
                            ))}
                            {DEC_LABELS.map(label => (
                                <g key={label.key} transform={`translate(${label.x} ${label.y})`}>
                                    <text className={cn(styles.text, styles.mono, styles.small, styles.fade)} textAnchor='middle' fill='var(--c-star-3)' style={place(0, 17, 1500, 900)}>
                                        {label.text}
                                    </text>
                                </g>
                            ))}
                            <path className={cn(styles.hair, styles.draw)} d={POLE_RING_PATH} pathLength={1} stroke='var(--c-brass-2)' style={timing(800, 1600)} />
                        </g>

                        <g aria-hidden>
                            {SEGMENTS.map(item => (
                                <path
                                    key={item.key}
                                    className={cn(styles.hair, styles.draw)}
                                    d={item.d}
                                    pathLength={1}
                                    stroke='var(--c-brass)'
                                    strokeOpacity={item.pointer ? 1 : 0.72}
                                    style={timing(item.start, item.duration)}
                                />
                            ))}

                            {DASHES.map(dash => (
                                <line
                                    key={dash.key}
                                    className={cn(styles.hair, styles.fade)}
                                    x1={dash.x}
                                    y1={dash.y}
                                    x2={dash.x2}
                                    y2={dash.y2}
                                    stroke='var(--c-brass)'
                                    strokeOpacity={0.85}
                                    style={timing(dash.delay, 380)}
                                />
                            ))}
                            {GAP_TICKS.map(tick => (
                                <line
                                    key={tick.key}
                                    className={cn(styles.hair, styles.fade)}
                                    x1={tick.x}
                                    y1={tick.y}
                                    x2={tick.x2}
                                    y2={tick.y2}
                                    stroke='var(--c-brass-2)'
                                    style={timing(tick.delay, 500)}
                                />
                            ))}
                            <g className={styles.caption} transform={`translate(${CAPTION.x} ${CAPTION.y}) rotate(${CAPTION.angle})`}>
                                <text className={cn(styles.text, styles.mono, styles.small, styles.fade)} textAnchor='middle' fill='var(--c-brass)' style={place(0, 0, CAPTION.delay, 1200)}>
                                    ABOUT FIVE TIMES THE GAP
                                </text>
                            </g>

                            <g className={cn(styles.inset, styles.fade)} style={timing(INSET.delay, 1400)}>
                                <line
                                    className={styles.hair}
                                    x1={INSET.leader.x1}
                                    y1={INSET.leader.y1}
                                    x2={INSET.leader.x2}
                                    y2={INSET.leader.y2}
                                    stroke='var(--c-hair-2)'
                                    strokeDasharray='2 4'
                                />
                                <circle className={styles.hair} cx={INSET.x} cy={INSET.y} r={INSET_RADIUS} stroke='var(--c-hair-2)' />
                                <circle cx={INSET.mizar.x} cy={INSET.mizar.y} r={3.2} fill='var(--c-star)' />
                                <circle cx={INSET.alcor.x} cy={INSET.alcor.y} r={1.7} fill='var(--c-star-2)' />
                                <g transform={`translate(${INSET.x} ${INSET.y - INSET_RADIUS})`}>
                                    <text className={cn(styles.text, styles.mono, styles.small)} textAnchor='middle' fill='var(--c-star-3)' style={place(0, -10)}>
                                        MIZAR · ALCOR
                                    </text>
                                </g>
                            </g>
                        </g>

                        {CHART_STARS.map((star, index) => (
                            <g
                                key={star.key}
                                ref={element => {
                                    starRefs.current[index] = element
                                }}
                                transform={`translate(${star.x} ${star.y})`}
                                className={styles.star}
                                tabIndex={index === focusIndex ? 0 : -1}
                                role='img'
                                aria-label={`${star.name}, ${star.note}`}
                                style={timing(star.delay)}
                                onFocus={() => {
                                    setFocusIndex(index)
                                    aimAt(star)
                                }}
                                onBlur={() => aimAt(null)}
                                onPointerEnter={() => aimAt(star)}
                                onPointerLeave={() => aimAt(null)}
                                onKeyDown={event => handleKey(event, index)}
                            >
                                <g className={styles.body}>
                                    <circle r={22} fill='transparent' />
                                    <circle className={cn(styles.glow, star.pole && styles.breathe)} r={round(star.r * 3)} fill='var(--c-brass)' style={glow(star.pole ? 0.24 : 0.15)} />
                                    <circle className={styles.core} r={star.r} fill='var(--c-star)' />
                                    <circle className={cn(styles.hair, styles.focusRing)} r={round(star.r + 7)} stroke='var(--c-brass)' />
                                </g>
                                {!star.pole && (
                                    <text
                                        aria-hidden
                                        className={cn(styles.text, styles.mono, star.pinned ? styles.fade : styles.name)}
                                        textAnchor={star.label[2]}
                                        fill={star.pinned ? 'var(--c-star-2)' : 'var(--c-star)'}
                                        style={place(star.label[0], star.label[1], PINNED_AT, 900)}
                                    >
                                        {star.name.toUpperCase()}
                                    </text>
                                )}
                            </g>
                        ))}

                        <g aria-hidden transform={`translate(${POLARIS_LABEL.x} ${POLARIS_LABEL.y})`}>
                            <text className={cn(styles.text, styles.mono, styles.fade)} fill='var(--c-brass)' style={place(0, -8, POLARIS_LABEL.delay, 1000)}>
                                POLARIS
                            </text>
                            <text className={cn(styles.text, styles.serif, styles.fade)} fill='var(--c-star-2)' style={place(0, 9, POLARIS_LABEL.delay + 200, 1200)}>
                                the North Star
                            </text>
                        </g>
                    </svg>
                </div>
            </div>
        </div>
    )
}

interface LearnHeroProps {
    /** The id of the guides section right after the hero; "Skip intro" lands there. */
    contentId: string
}

/**
 * Learn hero: a single constellation, quietly. A graduated ring and a declination scale draw in,
 * the stars of the Plough light one by one, the brass figure is drawn from the handle to the
 * pointers, and the pointer line runs on to Polaris while a short story fades in beside it.
 */
export const LearnHero = ({ contentId }: LearnHeroProps): ReactNode => (
    <HeroFrame
        id='learn-hero'
        label='Learn'
        coordinates={PLOUGH_CENTRE}
        screens={0}
        contentId={contentId}
        titleId={TITLE_ID}
        sky
    >
        <LearnScene />
    </HeroFrame>
)
